import React from 'react';
import {
  Combobox, Icon, type UserRole,
} from '@mairistem/finances-utils';
import type { Exercice } from '@mairistem/comptabilite';
import type { BordereauWithState } from '../../../../interfaces/pieces/TreeRow';
import BordereauModalProvider from './Modals/BordereauModalProvider';
import type TreeviewActionResult from '../../../../interfaces/pieces/TreeviewActionResult';
import type BordereauActionType from '../../../../interfaces/pieces/bordereaux/BordereauActionType';
import BordereauStateFactory from '../../../../classes/bordereaux/state/BordereauStateFactory';
import handleCheckAction from '../../../../utils/handleCheckAction';
import BaseLiquidationAction from '../../../../classes/pieces/BaseLiquidationAction';

interface BordereauActionProps {
  bordereau: BordereauWithState;
  exercice: Exercice;
  expanded: boolean;
  onChange: (rowData: BordereauWithState, shouldRefreshChildren?: boolean) => void;
  onDelete?: (rowData: BordereauWithState) => void;
  onAdd?: (rowData: BordereauWithState) => void;
  userRole: UserRole;
}

const BordereauAction = ({
  bordereau, exercice, expanded, onChange, onDelete, onAdd, userRole,
}: BordereauActionProps) => {
  const [forceVisible, setForceVisible] = React.useState<boolean>(false);

  const [
    currentActionBordereau,
    setCurrentActionBordereau,
  ] = React.useState<TreeviewActionResult<BordereauWithState> | null>(null);

  const allActions: BaseLiquidationAction<BordereauWithState, BordereauActionType>[] = bordereau.state.getBordereauActions(bordereau, userRole);

  const visibleActions = React.useMemo(
    () => allActions.filter((a) => (a.isShown(bordereau))),
    [allActions, bordereau],
  );

  const options = React.useMemo(
    () => visibleActions
      .map((action) => ({
        key: action.label,
        text: action.label,
        value: action.type,
        icon: action.isSuperUser && 'crown',
        'data-testid': `bordereauAction-${action.type}`,
        className: action.isActionPrincipale && visibleActions.length > 1 ? 'action-principale' : '',
      })),
    [visibleActions],
  );

  const handleChange = async (_e: React.BaseSyntheticEvent, { value }): Promise<void> => {
    /**
     * actionBordereau peut être :
     * - TreeviewActionResult<BordereauWithState>
     * - TreeviewActionResult<BordereauWithState>[]
     * - undefined (si l'action n'est pas trouvée)
     */
    const actionBordereau = bordereau.state
      .getBordereauActions(bordereau, userRole)
      .find((a) => a.type === value)
      ?.execute(bordereau, exercice, bordereau);

    await handleCheckAction(
      actionBordereau,
      (updatedBordereau) => ({
        ...updatedBordereau,
        montant: bordereau.montant,
        state: BordereauStateFactory.getState(updatedBordereau, exercice),
      }),
      onChange,
      onDelete,
      onAdd,
      setCurrentActionBordereau,
    );
  };

  /**
   * Validation de l'action
   */
  const handleChangeAction = async (bordereauActionType: BordereauActionType, data: any): Promise<void> => {
    // Exécution de l'action en back
    const actionBordereau = bordereau.state.getBordereauActions(bordereau, userRole).find(
      (a) => a.type === bordereauActionType,
    )?.executeAction({
      ...bordereau,
      ...data,
    }, exercice);

    // Modification de la TreeView en conséquence
    await handleCheckAction(
      actionBordereau,
      (updatedBordereau) => ({
        ...updatedBordereau,
        montant: bordereau.montant,
        state: BordereauStateFactory.getState(updatedBordereau, exercice),
      }),
      onChange,
      onDelete,
      onAdd,
      setCurrentActionBordereau,
    );
  };

  return (
    <div className={`bordereau-action ${(forceVisible || expanded) ? 'force-visible' : ''}`}>
      {currentActionBordereau && (
        <BordereauModalProvider
          treeViewAction={currentActionBordereau}
          onChange={handleChangeAction}
          bordereau={bordereau}
          exercice={exercice}
        />
      )}
      <Combobox
        options={options}
        icon={null}
        trigger={<Icon name="Menu" iconSet="Lucide" />}
        onChange={handleChange}
        direction="left"
        onOpen={() => setForceVisible(true)}
        onClose={() => setForceVisible(false)}
        value=""
        data-testid="bordereauActionCombobox"
      />
    </div>
  );
};

export default BordereauAction;
